import { useEffect, useMemo, useState } from "react";

import { OffCanvas } from "../../common/OffCanvas";
import PageTitle from "../../common/PageTitle";
import AutomationRulesService from "../../../services/automation-rules.service";
import ChecklistsService from "../../../services/checklists.service";
import ContactsService from "../../../services/contacts.service";
import DestinationService from "../../../services/destination.service";
import WorkOrderService from "../../../services/workorder.service";
import WorkSchedulesService from "../../../services/work_schedules.service";
import ZonesService from "../../../services/zones.service";
import { PRIORITY_OPTIONS } from "../../work_order/priorityOptions";
import AutomationIcon from "./AutomationIcon";
import {
  ASSIGNMENT_OPTIONS,
  REMINDER_OPTIONS,
  SCOPE_OPTIONS,
  TASK_TYPE_OPTIONS,
  TRIGGER_OPTIONS,
  createEmptyRule,
  createStatusStep,
  createTaskStep,
  normalizeFirstStepTiming,
  optionLabel,
} from "./automationRules.data";

const clone = (value) => JSON.parse(JSON.stringify(value));
const formatDate = (value) => value
  ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  : "Not run yet";
const apiError = (error) => error?.response?.data?.message
  || error?.response?.data?.error
  || error?.message
  || "Something went wrong";

const RuleStatus = ({ rule, onToggle, disabled = false }) => (
  <div className="autosend-checkbox" onClick={(event) => event.stopPropagation()}>
    <label className="setting-switch">
      <input
        type="checkbox"
        aria-label={`${rule.is_active ? "Pause" : "Activate"} ${rule.name}`}
        checked={Boolean(rule.is_active)}
        disabled={disabled}
        onChange={() => onToggle(rule)}
      />
      <span className="setting-slider setting-round" />
    </label>
  </div>
);

const MultiPicker = ({ label, options, selected, onChange, emptyText = "No options available" }) => {
  const [search, setSearch] = useState("");
  const selectedStrings = new Set((selected || []).map(String));
  const visible = options.filter((option) => (
    option.label.toLowerCase().includes(search.trim().toLowerCase())
  ));

  const toggle = (value) => {
    const key = String(value);
    onChange(selectedStrings.has(key)
      ? (selected || []).filter((item) => String(item) !== key)
      : [...(selected || []), value]);
  };

  return (
    <fieldset className="automation-multi-picker">
      <legend>{label}</legend>
      <div className="automation-picker-search">
        <AutomationIcon name="search" size={15} />
        <input
          type="search"
          aria-label={`Search ${label.toLowerCase()}`}
          placeholder={`Search ${label.toLowerCase()}`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div className="automation-picker-options">
        {visible.length ? visible.map((option) => (
          <label key={`${option.type || "option"}-${option.value}`}>
            <input
              type="checkbox"
              checked={selectedStrings.has(String(option.value))}
              onChange={() => toggle(option.value)}
            />
            <span>{option.label}</span>
          </label>
        )) : <p>{emptyText}</p>}
      </div>
      <span className="automation-picker-count">{selectedStrings.size} selected</span>
    </fieldset>
  );
};

const StepEditor = ({ step, index, total, checklists, assignees, onChange, onMove, onRemove }) => {
  const [expanded, setExpanded] = useState(index === 0);
  const update = (field, value) => onChange({ ...step, [field]: value });
  const updateTiming = (field, value) => update("timing", { ...step.timing, [field]: value });
  const updateAssignment = (field, value) => update("assignment", { ...step.assignment, [field]: value });
  const assigneeOptions = assignees.map((assignee) => ({
    value: `${assignee.type}:${assignee.id}`,
    label: assignee.label,
  }));
  const selectedAssignees = (step.assignment?.assignees || []).map(
    (assignee) => `${assignee.type}:${assignee.id}`,
  );

  return (
    <article className={`automation-step-editor${expanded ? " is-expanded" : ""}`}>
      <header>
        <span className="automation-step-number">{index + 1}</span>
        <button type="button" className="automation-step-toggle" aria-expanded={expanded} onClick={() => setExpanded((current) => !current)}>
          <span><strong>{step.name || "Untitled step"}</strong><small>{step.action_type === "create_task" ? "Task" : "Property status"}</small></span>
          <AutomationIcon name={expanded ? "chevron-up" : "chevron-down"} size={17} />
        </button>
        <div className="automation-step-controls">
          <button type="button" aria-label="Move step up" disabled={index === 0} onClick={() => onMove(-1)}>
            <AutomationIcon name="arrow-up" size={15} />
          </button>
          <button type="button" aria-label="Move step down" disabled={index === total - 1} onClick={() => onMove(1)}>
            <AutomationIcon name="arrow-down" size={15} />
          </button>
          <button type="button" aria-label="Delete step" disabled={total === 1} onClick={onRemove}>
            <AutomationIcon name="trash-2" size={15} />
          </button>
        </div>
      </header>

      {expanded && (
        <div className="automation-step-content">
      <div className="automation-editor-grid">
        <label>
          <span>Step name</span>
          <input className="uk-input" value={step.name} onChange={(event) => update("name", event.target.value)} />
        </label>
        <label>
          <span>Starts</span>
          <select
            className="uk-select"
            value={step.timing?.mode || "after_previous"}
            onChange={(event) => updateTiming("mode", event.target.value)}
          >
            {index > 0 && <option value="after_previous">After previous step</option>}
            <option value="reservation_relative">Relative to reservation</option>
            <option value="fixed">Fixed date and time</option>
          </select>
        </label>

        {step.timing?.mode === "reservation_relative" && (
          <>
            <label>
              <span>Reservation anchor</span>
              <select className="uk-select" value={step.timing.anchor || "checkout"} onChange={(event) => updateTiming("anchor", event.target.value)}>
                <option value="checkout">Checkout</option>
                <option value="checkin">Check-in</option>
                <option value="created">Reservation created</option>
              </select>
            </label>
            <label>
              <span>Local time</span>
              <input className="uk-input" type="time" value={step.timing.at_time || "09:00"} onChange={(event) => updateTiming("at_time", event.target.value)} />
            </label>
          </>
        )}
        {step.timing?.mode === "fixed" && (
          <>
            <label><span>Date</span><input className="uk-input" type="date" value={step.timing.date || ""} onChange={(event) => updateTiming("date", event.target.value)} /></label>
            <label><span>Local time</span><input className="uk-input" type="time" value={step.timing.at_time || "09:00"} onChange={(event) => updateTiming("at_time", event.target.value)} /></label>
          </>
        )}
        <label>
          <span>Offset in minutes</span>
          <input className="uk-input" type="number" value={step.timing?.offset_minutes || 0} onChange={(event) => updateTiming("offset_minutes", Number(event.target.value))} />
          <small>Use a negative number for before.</small>
        </label>
      </div>

      {step.action_type === "update_property_status" ? (
        <div className="automation-editor-grid automation-step-section">
          <label>
            <span>Set property status to</span>
            <select className="uk-select" value={step.property_status || "ready"} onChange={(event) => update("property_status", event.target.value)}>
              <option value="ready">Ready</option>
              <option value="needs_attention">Needs attention</option>
              <option value="out_of_service">Out of service</option>
            </select>
          </label>
        </div>
      ) : (
        <>
          <div className="automation-editor-grid automation-step-section">
            <label>
              <span>Task type</span>
              <select className="uk-select" value={step.task_type} onChange={(event) => update("task_type", event.target.value)}>
                {TASK_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label>
              <span>Priority</span>
              <select className="uk-select" value={step.priority || "medium"} onChange={(event) => update("priority", event.target.value)}>
                {PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="automation-span-two">
              <span>Task title</span>
              <input className="uk-input" value={step.summary} onChange={(event) => update("summary", event.target.value)} />
              <small>Available tokens: {"{property}, {guest}, {date}, {check_in}, {check_out}"}</small>
            </label>
            <label>
              <span>Expected duration</span>
              <div className="automation-input-suffix"><input className="uk-input" type="number" min="1" max="1440" value={step.duration_minutes || 60} onChange={(event) => update("duration_minutes", Number(event.target.value))} /><span>minutes</span></div>
            </label>
            <label>
              <span>Checklist</span>
              <select className="uk-select" value={step.checklist_template_id || ""} onChange={(event) => update("checklist_template_id", event.target.value ? Number(event.target.value) : null)}>
                <option value="">No checklist</option>
                {checklists.map((checklist) => <option key={checklist.id} value={checklist.id}>{checklist.name}</option>)}
              </select>
            </label>
            <label className="automation-span-two">
              <span>Description</span>
              <textarea className="uk-textarea" rows="2" value={step.description || ""} onChange={(event) => update("description", event.target.value)} />
            </label>
          </div>

          <div className="automation-step-section">
            <div className="automation-editor-grid">
              <label>
                <span>Assignment</span>
                <select className="uk-select" value={step.assignment?.strategy || "unassigned"} onChange={(event) => updateAssignment("strategy", event.target.value)}>
                  {ASSIGNMENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            </div>
            {step.assignment?.strategy !== "unassigned" && (
              <MultiPicker
                label={step.assignment.strategy === "available_pool" ? "Assignment pool" : "Assignees"}
                options={assigneeOptions}
                selected={selectedAssignees}
                onChange={(values) => updateAssignment("assignees", values.map((value) => {
                  const [type, id] = String(value).split(":");
                  return { type, id: Number(id), assignment_role: "assignee" };
                }))}
                emptyText="No taskers are available. Add team members or contacts first."
              />
            )}
          </div>

          <fieldset className="automation-reminders automation-step-section">
            <legend>Reminders</legend>
            {REMINDER_OPTIONS.map((option) => {
              const checked = (step.reminders || []).some((item) => Number(item.minutes_before) === option.value);
              return (
                <label key={option.value}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => update("reminders", checked
                      ? step.reminders.filter((item) => Number(item.minutes_before) !== option.value)
                      : [...(step.reminders || []), { minutes_before: option.value }])}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </fieldset>
        </>
      )}
        </div>
      )}
    </article>
  );
};

const RuleEditor = ({ open, rule, resources, saving, onClose, onSave, onTest }) => {
  const [draft, setDraft] = useState(createEmptyRule());
  const [error, setError] = useState("");
  const [reservationId, setReservationId] = useState("");

  useEffect(() => {
    if (open) {
      setDraft(rule ? clone(rule) : createEmptyRule());
      setError("");
      setReservationId("");
    }
  }, [open, rule]);

  const steps = draft.definition?.steps || [];
  const scopeOptions = resources[draft.scope_type] || [];
  const updateStep = (index, step) => {
    const next = [...steps];
    next[index] = step;
    setDraft({ ...draft, definition: { steps: next } });
  };
  const moveStep = (index, direction) => {
    const next = [...steps];
    const target = index + direction;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft({
      ...draft,
      definition: { steps: normalizeFirstStepTiming(next, draft.trigger_type) },
    });
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!draft.name.trim()) return setError("Give this workflow a name.");
    if (draft.scope_type !== "all_properties" && !draft.scope_values.length) {
      return setError("Choose at least one scope value.");
    }
    if (steps.some((step) => !step.name.trim())) return setError("Every step needs a name.");
    try {
      await onSave({
        ...draft,
        definition: { steps: normalizeFirstStepTiming(steps, draft.trigger_type) },
      });
    } catch (saveError) {
      setError(apiError(saveError));
    }
  };

  return (
    <OffCanvas open={open} close={onClose} contentClassName="automation-rule-editor-panel">
      <form className="automation-rule-editor" onSubmit={submit} aria-label="Automation rule editor">
        <header>
          <h2>{rule?.id ? "Edit Workflow" : "Add a Workflow"}</h2>
          <button type="button" className="automation-icon-button" aria-label="Close rule editor" onClick={onClose}><AutomationIcon name="x" size={20} /></button>
        </header>
        <div className="automation-editor-body">
          {error && <div className="automation-error" role="alert">{error}</div>}
          <section className="automation-editor-section">
            <h3>Workflow</h3>
            <div className="automation-editor-grid">
              <label className="automation-span-two"><span>Name</span><input className="uk-input" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
              <label className="automation-span-two"><span>Description</span><textarea className="uk-textarea" rows="2" value={draft.description || ""} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
              <label><span>Starts from</span><select className="uk-select" value={draft.trigger_type} onChange={(event) => {
                const triggerType = event.target.value;
                setDraft({
                  ...draft,
                  trigger_type: triggerType,
                  definition: {
                    steps: normalizeFirstStepTiming(steps, triggerType),
                  },
                });
              }}>{TRIGGER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            </div>
          </section>

          <section className="automation-editor-section">
            <h3>Property scope</h3>
            <div className="automation-editor-grid">
              <label><span>Apply to</span><select className="uk-select" value={draft.scope_type} onChange={(event) => setDraft({ ...draft, scope_type: event.target.value, scope_values: [] })}>{SCOPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            </div>
            {draft.scope_type !== "all_properties" && (
              <MultiPicker label={optionLabel(SCOPE_OPTIONS, draft.scope_type)} options={scopeOptions} selected={draft.scope_values || []} onChange={(values) => setDraft({ ...draft, scope_values: values })} />
            )}
          </section>

          <section className="automation-editor-section">
            <div className="automation-section-heading"><div><h3>Steps</h3><p>Steps run in order. Each step waits for the previous step to complete.</p></div><span>{steps.length}/20</span></div>
            <div className="automation-steps-list">
              {steps.map((step, index) => (
                <StepEditor
                  key={step.id}
                  step={step}
                  index={index}
                  total={steps.length}
                  checklists={resources.checklists}
                  assignees={resources.assignees}
                  onChange={(next) => updateStep(index, next)}
                  onMove={(direction) => moveStep(index, direction)}
                  onRemove={() => setDraft({ ...draft, definition: { steps: steps.filter((_, itemIndex) => itemIndex !== index) } })}
                />
              ))}
            </div>
            {steps.length < 20 && <div className="automation-add-step"><button type="button" onClick={() => setDraft({ ...draft, definition: { steps: [...steps, createTaskStep(steps.length)] } })}><AutomationIcon name="plus" size={15} /> Add task</button><button type="button" onClick={() => setDraft({ ...draft, definition: { steps: [...steps, createStatusStep()] } })}><AutomationIcon name="home" size={15} /> Add property status</button></div>}
          </section>

          {rule?.id && (
            <section className="automation-editor-section automation-test-run">
              <h3>Test this workflow</h3>
              <div><input className="uk-input" inputMode="numeric" placeholder="Reservation ID" value={reservationId} onChange={(event) => setReservationId(event.target.value)} /><button type="button" className="uk-button hms-btn hms-white-btn" disabled={!reservationId} onClick={() => onTest(Number(reservationId))}><AutomationIcon name="play" size={15} /> Run now</button></div>
            </section>
          )}
        </div>
        <footer>
          <div><button type="submit" className="uk-button hms-btn hms-red-btn" disabled={saving}>{saving ? "Saving..." : "Save"}</button><button type="button" className="uk-button hms-btn hms-cancel-btn" onClick={onClose}>Cancel</button></div>
        </footer>
      </form>
    </OffCanvas>
  );
};

const AutomationRules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [notification, setNotification] = useState("");
  const [pageError, setPageError] = useState("");
  const [resources, setResources] = useState({ properties: [], tags: [], markets: [], zones: [], checklists: [], assignees: [] });

  const load = async () => {
    setLoading(true);
    setPageError("");
    const results = await Promise.allSettled([
      AutomationRulesService.list(),
      WorkOrderService.getAttributes("", ["rentals", "tags"]),
      ChecklistsService.list({ page: 0, per_page: 500 }),
      WorkSchedulesService.listRecipientsWithSchedules({ page: 0, per_page: 500 }),
      ContactsService.getContacts(),
      DestinationService.getListDestinations(),
      ZonesService.getZones({ page: 0, per_page: 500 }),
    ]);
    const value = (index) => results[index].status === "fulfilled"
      ? (results[index].value?.data || {})
      : {};
    if (results[0].status === "rejected") setPageError(apiError(results[0].reason));
    const attributes = value(1);
    setRules(value(0).rules || []);
    setResources({
      properties: (attributes.rentals || []).map((item) => ({ value: Number(item.rental_id), label: item.pms_id ? `${item.pms_id} - ${item.address || item.name || "Property"}` : item.address || item.name || `Property ${item.rental_id}` })),
      tags: (attributes.tags || []).map((item) => ({ value: Number(item.id), label: item.name })),
      markets: (Array.isArray(value(5)) ? value(5) : value(5).destinations || []).map((item) => ({ value: Number(item.destination_id || item.id), label: item.name || item.destination_name || `Market ${item.destination_id || item.id}` })),
      zones: (value(6).zones || []).map((item) => ({ value: Number(item.id), label: item.name })),
      checklists: value(2).checklists || [],
      assignees: [
        ...(value(3).recipients || []).map((item) => ({
          id: Number(item.assignee_id || item.recipient_id || item.id),
          type: "recipient",
          label: item.recipient_name || item.name || [item.first_name, item.last_name].filter(Boolean).join(" ") || `Tasker ${item.assignee_id || item.recipient_id || item.id}`,
        })),
        ...(Array.isArray(value(4)) ? value(4) : value(4).contacts || []).map((item) => ({
          id: Number(item.id || item.contact_id),
          type: "contact",
          label: item.name || [item.first_name, item.last_name].filter(Boolean).join(" ") || item.email || `Contact ${item.id || item.contact_id}`,
        })),
      ],
    });
    setLoading(false);
  };

  useEffect(() => { document.title = "Turnover Workflows - RueBaRue"; load(); }, []);
  useEffect(() => {
    if (!notification) return undefined;
    const timeout = window.setTimeout(() => setNotification(""), 3000);
    return () => window.clearTimeout(timeout);
  }, [notification]);

  const filteredRules = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rules.filter((rule) => {
      const matchesStatus = status === "all" || (status === "active" ? rule.is_active : !rule.is_active);
      const text = [rule.name, rule.description, rule.trigger_type, rule.scope_type, ...(rule.definition?.steps || []).map((step) => step.name)].join(" ").toLowerCase();
      return matchesStatus && (!term || text.includes(term));
    });
  }, [rules, search, status]);

  const toggleRule = async (rule) => {
    try {
      const response = await AutomationRulesService.setActive(rule.id, !rule.is_active);
      setRules((current) => current.map((item) => item.id === rule.id ? { ...item, ...response.data.rule } : item));
      setNotification(rule.is_active ? "Workflow paused" : "Workflow activated");
    } catch (error) { setPageError(apiError(error)); }
  };
  const saveRule = async (draft) => {
    setSaving(true);
    try {
      const response = draft.id
        ? await AutomationRulesService.update(draft.id, draft)
        : await AutomationRulesService.create(draft);
      const saved = response.data.rule;
      setRules((current) => draft.id ? current.map((item) => item.id === saved.id ? saved : item) : [saved, ...current]);
      setEditorOpen(false);
      setSelectedRule(null);
      setNotification(draft.id ? "Workflow updated" : "Workflow created");
    } finally { setSaving(false); }
  };
  const deleteRule = async (rule) => {
    if (!rule || !window.confirm(`Delete "${rule.name}"? Generated tasks will remain in My Tasks.`)) return;
    await AutomationRulesService.remove(rule.id);
    setRules((current) => current.filter((item) => item.id !== rule.id));
    setNotification("Workflow deleted");
  };
  const testRule = async (reservationId) => {
    try {
      const response = await AutomationRulesService.reconcileReservation(
        reservationId,
        selectedRule?.id,
      );
      const created = (response.data.result?.runs || []).flatMap((run) => run.steps || []).filter((step) => step.status === "created").length;
      setNotification(created ? `${created} task${created === 1 ? "" : "s"} created` : "Workflow reconciled; no duplicate tasks created");
    } catch (error) { setPageError(apiError(error)); }
  };

  return (
    <>
      <section className="automation-rules-page">
        <PageTitle title="Turnover Workflows" />
        <div id="hms-main-body">
          {pageError && <div className="automation-error" role="alert"><span>{pageError}</span><button type="button" onClick={() => setPageError("")} aria-label="Dismiss error"><AutomationIcon name="x" size={16} /></button></div>}
          <div className="uk-flex uk-flex-between uk-flex-top automation-workflow-actions">
            <div className="hms-guest-btn">
              <button type="button" className="uk-button hms-btn hms-red-btn" onClick={() => { setSelectedRule(null); setEditorOpen(true); }}><span uk-icon="icon: plus; ratio: 0.7" /> Workflow</button>
            </div>
          </div>
          <div className="uk-card uk-card-small uk-card-default hms-form-card automation-rules-card">
            <div className="uk-overflow-auto">
              <form className="uk-form" onSubmit={(event) => event.preventDefault()}>
                <div className="uk-flex uk-flex-between uk-flex-top workorder-form-top-filters automation-workflow-filters">
                  <div className="hms-form hms-form-inline uk-margin-small-bottom">
                    <div className="rbr-pill-toggle rbr-task-status-toggle" role="group" aria-label="Workflow status">{["all", "active", "inactive"].map((value) => <button type="button" key={value} className={`rbr-pill-toggle-button${status === value ? " is-active" : ""}`} aria-pressed={status === value} onClick={() => setStatus(value)}>{value.charAt(0).toUpperCase() + value.slice(1)}</button>)}</div>
                    <div className="uk-inline hms-search-inline no-margin">
                      <div className="uk-search uk-search-default">
                        {search ? (
                          <button type="button" className="uk-form-icon-flip uk-form-icon automation-search-clear" aria-label="Clear search" onClick={() => setSearch("")}><AutomationIcon name="x" size={15} /></button>
                        ) : <span className="uk-search-icon-flip" uk-search-icon="true" />}
                        <input className="uk-search-input" type="search" aria-label="Search workflows" placeholder="Workflow name or step" value={search} onChange={(event) => setSearch(event.target.value)} />
                      </div>
                    </div>
                  </div>
                </div>
              </form>
              <table className="uk-table uk-table-hover uk-table-divider hms-table automation-rules-table">
                <thead><tr><th>Active</th><th className="uk-table-expand name-field">Workflow</th><th className="uk-table-expand">Scope</th><th className="uk-table-expand">Starts from</th><th className="uk-table-expand">Sequence</th><th>Properties</th><th className="uk-width-205 actions">ACTIONS</th></tr></thead>
                <tbody>
                  {loading ? <tr><td colSpan="7" className="automation-table-message">Loading workflows...</td></tr> : filteredRules.length ? filteredRules.map((rule) => (
                    <tr key={rule.id} tabIndex="0" onClick={() => { setSelectedRule(rule); setEditorOpen(true); }} onKeyDown={(event) => { if (event.key === "Enter") { setSelectedRule(rule); setEditorOpen(true); } }}>
                      <td><RuleStatus rule={rule} onToggle={toggleRule} /></td>
                      <td><div className="automation-workflow-cell"><span className="automation-type-icon is-workflow"><AutomationIcon name="layers" size={16} /></span><div><strong>{rule.name}</strong><span>Updated {formatDate(rule.updated_at)}</span></div></div></td>
                      <td><span className="automation-type-badge">{optionLabel(SCOPE_OPTIONS, rule.scope_type)}</span></td>
                      <td>{optionLabel(TRIGGER_OPTIONS, rule.trigger_type)}</td>
                      <td><div className="automation-sequence">{(rule.definition?.steps || []).slice(0, 4).map((step, index) => <span key={step.id}>{index > 0 && <AutomationIcon name="chevron-right" size={13} />}{step.name}</span>)}{(rule.definition?.steps || []).length > 4 && <em>+{rule.definition.steps.length - 4}</em>}</div></td>
                      <td>{rule.scope_type === "all_properties" ? "All" : Number(rule.matched_property_count || 0).toLocaleString()}</td>
                      <td className="hms-action-btn"><div className="uk-flex uk-flex-top"><div className="uk-inline"><button type="button" className="uk-button" aria-label={`Edit ${rule.name}`} uk-tooltip="Edit" onClick={(event) => { event.stopPropagation(); setSelectedRule(rule); setEditorOpen(true); }}><img src="/images/edit.svg" alt="" /></button></div><div className="uk-inline"><button type="button" className="uk-button" aria-label={`Delete ${rule.name}`} uk-tooltip="Delete" onClick={(event) => { event.stopPropagation(); deleteRule(rule); }}><img src="/images/delete.svg" alt="" /></button></div></div></td>
                    </tr>
                  )) : <tr><td colSpan="7" className="automation-table-message">No workflows matching your current search or filters.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
      {editorOpen && <RuleEditor open={editorOpen} rule={selectedRule} resources={resources} saving={saving} onClose={() => { setEditorOpen(false); setSelectedRule(null); }} onSave={saveRule} onTest={testRule} />}
      {notification && <div className="automation-toast" role="status"><AutomationIcon name="check-circle" size={17} />{notification}</div>}
    </>
  );
};

export default AutomationRules;
